import { useState } from "react";

import { type SxProps as MuiSxProps } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";

import type { BoxProps } from "./Box";


type Props = {
	src?: string;
	color?: string;
	fallback?: string;
} & BoxProps;

type CoverProps = {
	size?: number;
	src?: string;
	fallback?: string;
	navigating?: boolean;
	sx?: MuiSxProps
};

Image.Cover = ({ src, navigating, fallback, size = 160, sx }: CoverProps) => {
	const style = {
		thumbBg: {
			position: "relative",
			height: size,
			aspectRatio: "3/2",
			backgroundImage: `url(${src})`,
			backgroundPosition: "center",
			backgroundSize: "cover",
			...sx
		},
		thumb: {
			height: size,
			aspectRatio: "3/2",
			objectFit: "contain",
			backdropFilter: "blur(10px)"
		}
	} as SxProps;
	return (
		<Box sx={style.thumbBg}>
			<Image src={src} fallback={fallback} sx={style.thumb} />
			{navigating && <CircularProgress size={40} thickness={5} color="tertiary" sx={style.loading} />}
		</Box>
	);
};

export default function Image({ src, color, fallback, sx, ...props }: Props) {
	const [loaded, setLoaded] = useState(src ? false : true);

	const style = {
		skeleton: {
			display: !loaded ? "block" : "none",
			backgroundColor: color,
			...sx
		},
		image: {
			display: loaded ? "block" : "none",
			...sx
		}
	} as SxProps;

	if (!src && !fallback) return null;
	return (
		<>
			{!loaded && <Skeleton variant="rectangular" sx={style.skeleton} />}
			<Box
				component="img"
				src={src || fallback}
				{...props}
				sx={style.image}
				onLoad={() => setLoaded(true)}
				onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
					if (fallback) {
						e.currentTarget.src = fallback;
						e.currentTarget.style.objectFit = "cover";
					}
				}}
			/>
		</>
	);
}